import { motion } from "framer-motion";
import React, { forwardRef, useId, useMemo } from "react";

import { cn } from "@/lib/utils";

export type SegmentedToggleInputProps = {
  readonly id?: string;
  readonly name: string;
  readonly value: string;
  readonly options: { value: string; label: string }[];
  readonly onChange?: (value: string) => void;
  readonly onBlur?: () => void;
  readonly onFocus?: () => void;
  readonly size?: "xs" | "sm" | "md" | "lg";
  readonly disabled?: boolean;
  readonly className?: string;
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby": string;
  readonly "data-testid"?: string;
};

const SegmentedToggleInput = forwardRef<
  HTMLDivElement,
  SegmentedToggleInputProps
>((props, ref): React.ReactElement => {
  const {
    id,
    name,
    value,
    options,
    onChange,
    onBlur,
    onFocus,
    size = "md",
    disabled = false,
    className = "",
    "data-testid": dataTestId,
  } = props;

  const generatedId = useId();
  const inputId = id || generatedId;

  const sizes = {
    xs: {
      container: "p-0.5",
      segment: "px-3 py-0.5 text-xs",
      thumb: "rounded-full",
    },
    sm: {
      container: "p-1",
      segment: "px-3 py-1 text-sm",
      thumb: "rounded-full",
    },
    md: {
      container: "p-1.5",
      segment: "px-4 py-2 text-base",
      thumb: "rounded-full",
    },
    lg: {
      container: "p-2",
      segment: "px-5 py-2.5 text-lg",
      thumb: "rounded-full",
    },
  } as const;

  const idxMap = useMemo(
    () => Object.fromEntries(options.map((o, i) => [o.value, i])),
    [options]
  );
  const activeIndex = idxMap[value] ?? 0;

  const setValue = (v: string) => {
    if (disabled) return;
    onChange?.(v);
  };

  // Keyboard navigation: arrows and Home/End
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const current = activeIndex;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = (current + 1) % options.length;
      setValue(options[next].value);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const prev = (current - 1 + options.length) % options.length;
      setValue(options[prev].value);
    } else if (e.key === "Home") {
      e.preventDefault();
      setValue(options[0].value);
    } else if (e.key === "End") {
      e.preventDefault();
      setValue(options[options.length - 1].value);
    }
  };

  const handleFocus = () => {
    onFocus?.();
  };

  const handleBlur = () => {
    onBlur?.();
  };

  return (
    <div
      ref={ref}
      aria-disabled={disabled}
      aria-invalid={props["aria-invalid"]}
      aria-describedby={props["aria-describedby"]}
      className={cn(
        "SegmentedToggleInput inline-flex w-full gap-0 rounded-full bg-background-two",
        sizes[size].container,
        disabled ? "cursor-not-allowed opacity-50" : "",
        props["aria-invalid"] && "ring-1 ring-red",
        className
      )}
      data-testid={dataTestId}
      onKeyDown={onKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div
        className="relative grid w-full"
        style={{
          gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
        }}
      >
        <motion.div
          layout
          layoutId={`${inputId}-thumb`}
          transition={{ type: "spring", bounce: 0.25, duration: 0.3 }}
          className={cn(
            "absolute inset-y-0",
            sizes[size].thumb,
            "bg-black shadow-sm"
          )}
          style={{
            left: `calc((100% / ${options.length}) * ${activeIndex})`,
            width: `calc(100% / ${options.length})`,
          }}
        />

        {options.map((option, i) => {
          const isActive = value === option.value;
          return (
            <button
              key={option.value}
              id={`${inputId}-${i}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${inputId}-panel-${i}`}
              className={cn(
                "relative select-none whitespace-nowrap",
                "rounded-full",
                sizes[size].segment,
                "transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2",
                isActive ? "text-white" : "text-neutral-700",
                disabled && "cursor-not-allowed"
              )}
              disabled={disabled}
              type="button"
              onClick={() => setValue(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
});

SegmentedToggleInput.displayName = "SegmentedToggleInput";

export default SegmentedToggleInput;
